import { makeStyles, Typography } from "@material-ui/core"
import dayjs from "dayjs"
import { FC, useEffect, useState } from "react"
import {
  ClientFragment,
  useQuestionnaireTakesSinceDateByUserIdQuery,
} from "../../../generated/graphql"
import { useGraphQLClient } from "../../../hooks/useGraphQLClient"
import { colors } from "../../../utils/theme"
import {
  ChartDataPoints,
  QuestionnaireResponseProps,
} from "../../../utils/types"
import QuestionnaireChart from "../../molecules/QuestionnaireChart"
import RiskAssessmentQuestionnaireChart from "../../molecules/RiskAssessmentQuestionnaireChart"
import {
  getRiskAssessmentChartLines,
  getRiskAssessmentQuestion,
  getRiskAssessmentResponseValue,
} from "../../../utils/helpers"

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    borderRadius: 4,
    border: `1px solid ${colors.neutral.clay}`,
    padding: theme.spacing(3),
    maxWidth: 400,
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  chartDateRange: {
    fontStyle: "italic",
    fontWeight: 400,
    color: colors.neutral.denim,
    opacity: 0.8,
  },
  chartLastResultDate: {
    fontWeight: 400,
    color: colors.neutral.denim,
    opacity: 0.8,
  },
  chartTitle: {
    fontWeight: 400,
    color: colors.neutral.denim,
    marginBottom: 4,
  },
  chartsFlexContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    flexDirection: "row",
  },
}))

interface Chart {
  name: string
  id: number
  lastResultDate: string
  data: ChartDataPoints[]
  hasRiskQuestions?: boolean
  scoreType?: string
}

const QuestionnaireChartsContainer: FC<{
  client: ClientFragment
  chartCallbackData: Function
}> = ({ client, chartCallbackData }) => {
  const classes = useStyles()
  const [charts, setCharts] = useState<Chart[]>([])
  const [hasIncompleteQuestionnaires, setHasIncompleteQuestionnaires] =
    useState<boolean>(true)
  const [riskAssessmentQuestion, setRiskAssessmentQuestion] =
    useState<string>("")
  const [chartLines, setChartLines] = useState<string[]>([])

  const graphQLClient = useGraphQLClient()
  const { data: recentQuestionnaires } =
    useQuestionnaireTakesSinceDateByUserIdQuery(graphQLClient, {
      userId: client.id,
      sinceDate: dayjs().subtract(6, "months").format("YYYY-MM-DD"),
    })

  useEffect(() => {
    // Map of charts by id (of type number) with data and name
    const chartsByType = new Map<
      number,
      {
        name: string
        data: ChartDataPoints[]
        hasRiskQuestions?: boolean
        scoreType: string
      }
    >()
    let noCompleteTakes = true
    // check if there are no recent questionnaires and leave
    if (!recentQuestionnaires) {
      setHasIncompleteQuestionnaires(true)
      chartCallbackData({
        chartLength: 0,
        hasIncompleteQuestionnaires,
        questionnaireTakes: [],
      })
      return
    }

    // for every recent questionnaire, make a data point
    recentQuestionnaires.questionnaire_takes.forEach((take) => {
      if (take.completion_status !== "complete") {
        return
      } else {
        noCompleteTakes = false
        setHasIncompleteQuestionnaires(false)
      }
      const hasRiskQuestions =
        take.questionnaire_schedule?.questionnaire?.contains_risk_questions ??
        false

      const questionnaireResponses =
        take.questionnaire_responses as QuestionnaireResponseProps[]

      // 1 is the display order for the requested risk assessment question
      if (hasRiskQuestions) {
        const riskAssessmentQuestion = getRiskAssessmentQuestion(
          questionnaireResponses,
          1
        )
        setRiskAssessmentQuestion(riskAssessmentQuestion)
        const chartLinesValues = getRiskAssessmentChartLines(1)
        setChartLines(chartLinesValues)
      }

      let y = hasRiskQuestions
        ? getRiskAssessmentResponseValue(questionnaireResponses, 1)
        : take.questionnaireDisplayValues?.display_score ?? 0
      const x = take.completed_at
      const date = dayjs(take.completed_at).format("MMM D, YYYY")
      const name = hasRiskQuestions
        ? take.questionnaire_schedule?.questionnaire.name ?? ""
        : take.questionnaireDisplayValues?.questionnaire_name ?? ""
      const id = hasRiskQuestions
        ? take.questionnaire_schedule?.questionnaire.id!
        : take.questionnaireDisplayValues?.questionnaire_id! // we check for this above
      const color =
        take.questionnaireDisplayValues?.scoring_display_color ?? "grey"
      const other =
        take.questionnaireDisplayValues?.scoring_display_name ?? undefined
      const scoreType =
        take.questionnaireDisplayValues?.display_score_type ?? ""

      if (!chartsByType.has(id)) {
        chartsByType.set(id, { name: name, data: [], scoreType: scoreType })
      }
      const chart = chartsByType.get(id)! // we just set this above
      chart.data.push({
        x,
        y,
        stroke: color,
        fill: `${color}80`, // make it 50% opaque
        date,
        label: "",
        other,
      })
      chart.hasRiskQuestions = hasRiskQuestions
      chartsByType.set(id, chart)
    })

    // if there are no complete takes, leave
    if (noCompleteTakes) {
      setCharts([])
      chartCallbackData({
        chartLength: 0,
        hasIncompleteQuestionnaires,
        questionnaireTakes: recentQuestionnaires.questionnaire_takes,
      })
      return
    }
    const charts = Array.from(chartsByType.keys()).map((key) => {
      // get the last 8 sorted by date
      const data = chartsByType
        .get(key)!
        .data.sort((a, b) => (a.x > b.x ? 1 : -1))
        .slice(-8)

      return {
        id: key,
        data,
        name: chartsByType.get(key)!.name,
        lastResultDate: data[data.length - 1].x,
        hasRiskQuestions: chartsByType.get(key)!.hasRiskQuestions,
        scoreType: chartsByType.get(key)?.scoreType ?? "",
      }
    })
    setCharts(charts)
    chartCallbackData({
      chartLength: charts.length,
      hasIncompleteQuestionnaires,
      questionnaireTakes: recentQuestionnaires.questionnaire_takes,
    })
  }, [chartCallbackData, hasIncompleteQuestionnaires, recentQuestionnaires])

  return (
    <div className={classes.chartsFlexContainer}>
      {charts.map((chart) => (
        <div key={chart.name} className={classes.chartContainer}>
          <div className={classes.chartHeader}>
            <div>
              <Typography variant="h5" className={classes.chartTitle}>
                {chart.name}
              </Typography>
              <Typography
                variant="caption"
                className={classes.chartLastResultDate}
              >
                Last Result:{" "}
                {dayjs(chart.lastResultDate).format("MMM DD, YYYY")}
              </Typography>
            </div>
            <Typography variant="caption" className={classes.chartDateRange}>
              Last {chart.data.length} questionnaires
            </Typography>
          </div>
          <div>
            {chart.hasRiskQuestions && (
              <Typography variant="caption">
                {riskAssessmentQuestion}
              </Typography>
            )}
          </div>
          {chart.hasRiskQuestions ? (
            <RiskAssessmentQuestionnaireChart
              data={chart.data}
              id={chart.id}
              chartLines={chartLines}
            />
          ) : (
            <QuestionnaireChart
              data={chart.data}
              id={chart.id}
              scoreType={chart.scoreType ?? ""}
            />
          )}
        </div>
      ))}
    </div>
  )
}

export default QuestionnaireChartsContainer
